import { useState, useEffect, useMemo } from 'react';
import {
  subscribeToActiveAnnouncements,
  subscribeToDismissedAnnouncements,
  dismissAnnouncement,
} from '@shared/services/announcementService';
import type { AnnouncementBanner } from '@shared/types';
import { useAuth } from '../context/AuthContext';

/**
 * Subscribes to active announcements and user dismissals,
 * returning only non-dismissed banners.
 */
export function useAnnouncements() {
  const { currentUser } = useAuth();
  const [announcements, setAnnouncements] = useState<AnnouncementBanner[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const unsub = subscribeToActiveAnnouncements(setAnnouncements);
    return unsub;
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = subscribeToDismissedAnnouncements(currentUser.uid, setDismissedIds);
    return unsub;
  }, [currentUser?.uid]);

  const visibleAnnouncements = useMemo(
    () => announcements.filter((a) => !dismissedIds.has(a.id)),
    [announcements, dismissedIds],
  );

  const dismiss = async (announcementId: string) => {
    if (!currentUser?.uid) return;
    // Optimistic update
    setDismissedIds((prev) => new Set([...prev, announcementId]));
    await dismissAnnouncement(currentUser.uid, announcementId);
  };

  return { announcements: visibleAnnouncements, dismiss };
}
